import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, type Project, thumbOf, mediaSrc } from "../../lib/api";
import { formatProjectYmd } from "../../lib/dates";

export function AdminProjects() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [q, setQ] = useState("");
  const [visibility, setVisibility] = useState("");
  const [loading, setLoading] = useState(true);

  function load() {
    setLoading(true);
    api
      .adminProjects({ q: q || undefined, visibility: visibility || undefined, perPage: 100 })
      .then((r) => setProjects(r.data))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibility]);

  async function remove(id: string, title: string) {
    if (!confirm(`Delete “${title}”? This cannot be undone.`)) return;
    await api.deleteProject(id);
    load();
  }

  return (
    <div className="admin-page">
      <h1>Projects</h1>
      <p className="admin-page__sub">Create, edit, publish, and reorder portfolio entries.</p>
      <div className="admin-page__toolbar">
        <Link to="/admin/projects/new" className="btn btn--primary">
          New project
        </Link>
        <input
          placeholder="Search…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          style={{
            background: "var(--bg-elevated)",
            border: "1px solid var(--border)",
            borderRadius: 8,
            padding: "0.55rem 0.8rem",
          }}
        />
        <select
          value={visibility}
          onChange={(e) => setVisibility(e.target.value)}
          style={{
            background: "var(--bg-elevated)",
            border: "1px solid var(--border)",
            borderRadius: 8,
            padding: "0.55rem 0.8rem",
          }}
        >
          <option value="">All visibility</option>
          <option value="published">Published</option>
          <option value="draft">Draft</option>
          <option value="private">Private</option>
        </select>
        <button type="button" className="btn btn--ghost btn--sm" onClick={load}>
          Refresh
        </button>
      </div>

      {loading ? (
        <p>Loading…</p>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th></th>
              <th>Title</th>
              <th>Date</th>
              <th>Status</th>
              <th>Priority</th>
              <th>Categories</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {projects.map((p) => (
              <tr key={p.id}>
                <td>
                  {thumbOf(p) ? (
                    <img className="thumb" src={mediaSrc(thumbOf(p))} alt="" />
                  ) : (
                    "—"
                  )}
                </td>
                <td>
                  <Link to={`/admin/projects/${p.id}`}>{p.title}</Link>
                  {p.featured && (
                    <span className="badge" style={{ marginLeft: 8 }}>
                      Featured
                    </span>
                  )}
                </td>
                <td style={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                  {formatProjectYmd(p.date)}
                </td>
                <td>
                  <span className={`vis-badge vis-badge--${p.visibility}`}>{p.visibility}</span>
                </td>
                <td>{p.displayPriority}</td>
                <td style={{ color: "var(--text-dim)", fontSize: "0.85rem" }}>
                  {p.categories.map((c) => c.name).join(", ") || "—"}
                </td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <Link to={`/admin/projects/${p.id}`} className="btn btn--ghost btn--sm">
                    Edit
                  </Link>
                  {p.visibility === "published" && (
                    <a
                      href={`/project/${p.slug}`}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn--ghost btn--sm"
                    >
                      View
                    </a>
                  )}
                  <button
                    type="button"
                    className="btn btn--danger btn--sm"
                    onClick={() => remove(p.id, p.title)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
